// ─────────────────────────────────────────────────────────────────────────────
// Central plan / tier configuration — SINGLE SOURCE OF TRUTH (frontend).
//
// Add a new tier or change a price by editing PLANS below. The PHP mirror in
// `public/dyn/conf/plans.conf.php` MUST be kept in sync (it re-validates price +
// resolves plan_id server-side — never trust the client price).
//
// Payment model:
//   type "subscription" → true recurring PayPal billing. Needs a paypalPlanId
//     (a billing plan created in the PayPal Dashboard for that price+interval).
//   type "onetime"      → single capture via process-order.php / Orders. No plan.
//
// Sandbox and live are separate PayPal accounts holding separate plan ids, so every
// subscription tier carries BOTH and the build picks one — the same switch that picks
// the client id. Mixing them is not a visible mistake: PayPal answers a live checkout
// carrying a sandbox plan with RESOURCE_NOT_FOUND and the sale is simply lost.
//
// NOTE (Key API identity): the Key API groups products by `appName` (all TV apps →
// "ACCTV", all drive apps → "ACCDRIVE") and identifies the specific app via the
// `features` bitmask. LMUTV is a TV app → appName "ACCTV", feature bit 512.
// ─────────────────────────────────────────────────────────────────────────────

import { PAYPAL_SANDBOX } from "./paypal";

export type PlanType = "subscription" | "onetime";

export interface PlanTier {
  /** App slug — matches the storefront + backend app key. */
  app: string;
  /** Tier within the app, e.g. "standard" | "pro". */
  edition: string;
  /** Duration key, e.g. "3m" | "1y" | "lifetime". */
  duration: string;
  /** Recurring subscription or single one-time purchase. */
  type: PlanType;
  /** Display / charge price in EUR (gross). Server re-validates. */
  price: number;
  /** Billing interval in months for subscriptions; null for one-time/lifetime. */
  intervalMonths: number | null;
  /**
   * PayPal billing plan id (P-XXXX...) for subscriptions, resolved for the current
   * environment. null for one-time tiers, and for a tier that has no plan in this
   * environment (the 1d test tiers exist in sandbox only). A subscription tier with
   * a null paypalPlanId is treated as "not yet purchasable".
   */
  paypalPlanId: string | null;
  /** Feature bitmask handed to the Key API on issuance. */
  features: number;
  /** Optional human label; falls back to i18n if omitted. */
  label?: string;
}

// LMUTV feature bits: base app = 512, Pro extra = 1024. A Pro key is additive
// (base | pro = 1536) so it still matches the "is LMUTV" filter (bit 512) while
// unlocking Pro (bit 1024).
const LMUTV_STANDARD_FEATURES = 512;
const LMUTV_PRO_FEATURES = 512 | 1024; // 1536

/**
 * Pick the plan id for the environment this bundle was built for. A tier that does
 * not exist in the current environment passes null and is then not purchasable —
 * which is what the 1d test tiers do in a release build.
 */
const planId = (sandbox: string | null, live: string | null): string | null =>
  PAYPAL_SANDBOX ? sandbox : live;

// PayPal plans are tiered (quantity_supported + TIERED pricing_scheme): the 4th PC
// and beyond bill at 50%. The checkout passes quantity (PC count) to
// actions.subscription.create — PayPal computes the bulk. `price` here is the
// single-unit display price.
export const PLANS: PlanTier[] = [
  // ── LMUTV Standard ──────────────────────────────────────────────────────────
  {
    // test-only: daily billing to watch renewal/grant-extension fast (sandbox).
    app: "lmutv", edition: "standard", duration: "1d", type: "subscription",
    price: 18, intervalMonths: null, label: "LMU TV (1 day, test)",
    paypalPlanId: planId("P-0GW992911D1058243NJZT75Q", null), // LMUTV 1D — sandbox only (test tier)
    features: LMUTV_STANDARD_FEATURES,
  },
  {
    app: "lmutv", edition: "standard", duration: "3m", type: "subscription",
    price: 18, intervalMonths: 3,
    paypalPlanId: planId("P-6U183497BD654681KNJZT7EQ", "P-7V334307BH8247255NKS3P6Y"), // LMU TV 90D
    features: LMUTV_STANDARD_FEATURES,
  },
  {
    app: "lmutv", edition: "standard", duration: "1y", type: "subscription",
    price: 65, intervalMonths: 12,
    paypalPlanId: planId("P-3WS35751WS0080331NJZT6ZQ", "P-9CT8358066389354MNKS3QQA"), // LMU TV 12M
    features: LMUTV_STANDARD_FEATURES,
  },
  {
    app: "lmutv", edition: "standard", duration: "lifetime", type: "onetime",
    price: 120, intervalMonths: null,
    paypalPlanId: null,
    features: LMUTV_STANDARD_FEATURES,
  },
  // ── LMUTV Pro ────────────────────────────────────────────────────────────────
  {
    // test-only: daily billing (sandbox).
    app: "lmutv", edition: "pro", duration: "1d", type: "subscription",
    price: 30, intervalMonths: null, label: "LMU TV Pro (1 day, test)",
    paypalPlanId: planId("P-7CE78471EV6080108NJ4RTUQ", null), // LMUTV Pro 1D — sandbox only (test tier)
    features: LMUTV_PRO_FEATURES,
  },
  {
    app: "lmutv", edition: "pro", duration: "3m", type: "subscription",
    price: 30, intervalMonths: 3,
    paypalPlanId: planId("P-67V9724487360604RNJ4RUEI", "P-7JA22669FV136463DNKS3Q4Q"), // LMU TV PRO 90D
    features: LMUTV_PRO_FEATURES,
  },
  {
    app: "lmutv", edition: "pro", duration: "1y", type: "subscription",
    price: 110, intervalMonths: 12,
    paypalPlanId: planId("P-5W95609131413500CNJ4RUNA", "P-94803313RG596502BNKS3RIY"), // LMU TV PRO 12M
    features: LMUTV_PRO_FEATURES,
  },
  {
    app: "lmutv", edition: "pro", duration: "lifetime", type: "onetime",
    price: 190, intervalMonths: null,
    paypalPlanId: null,
    features: LMUTV_PRO_FEATURES,
  },
];

/** Look up a single tier. Returns undefined if the combination is not configured. */
export function findPlan(
  app: string,
  edition: string,
  duration: string
): PlanTier | undefined {
  return PLANS.find(
    (p) => p.app === app && p.edition === edition && p.duration === duration
  );
}

/** All tiers for an app (e.g. to render a pricing grid). */
export function plansForApp(app: string): PlanTier[] {
  return PLANS.filter((p) => p.app === app);
}

/** True when a subscription tier is actually purchasable (has a live plan_id). */
export function isPurchasable(p: PlanTier): boolean {
  return p.type === "onetime" || !!p.paypalPlanId;
}
